import { FC, InputHTMLAttributes, useMemo } from 'react'
import { useFormContext, FieldError, FieldErrors } from 'react-hook-form'
import styles from 'components/atoms/inputs/RadioButton/RadioButton.styl'
import cn from 'classnames'
import Flex from 'components/atoms/Flex/Flex'

type RadioButtonProps =
  & Omit<InputHTMLAttributes<HTMLInputElement>, 'name' | 'value'>
  & {
    name: string
    label?: string
    hint?: string
    value: string | number
    displayValue?: string
    text?: string | number
    wrapperClassName?: string
    dotClassName?: string
    errors?: FieldErrors
    withBorder?: boolean
    isHighlighted?: boolean // Can be highlighted even if not selected
  }

/**
 * Designed to work with react-hook-form via useFormContext()
 * Should be a descendant of <FormProvider>
 */
export const RadioButton: FC<RadioButtonProps> = (props) => {
  const {
    name,
    text,
    label,
    hint,
    children,
    value,
    className,
    wrapperClassName,
    dotClassName,
    errors,
    withBorder,
    isHighlighted,
    displayValue,
    disabled,
    ...restProps
  } = props

  const fieldError: FieldError = !!errors && errors[name]
  const { watch, register } = useFormContext()
  const currentValue = watch(name)
  const isSelected = useMemo(() => value === currentValue, [currentValue, value])

  const hiddenRadioButton = useMemo(() => (
    <input
      aria-hidden='true'
      className={ cn(styles.input, className) }
      type='radio'
      value={ value }
      { ...register(name) }
      { ...restProps }
      readOnly
      disabled={ disabled }
    />
  ), [className, value, register, name, restProps, disabled])

  const hintChild = useMemo(() => !!hint && <small>{ hint }</small>, [hint])

  const textChild = useMemo(() => !!text && <p>{text}</p>, [text])

  const labelTextChild = useMemo(() => !!label && (
    <span className={ styles.labelText }>
      {label}
    </span>
  ), [label])

  const fieldErrorChild = useMemo(() => !!fieldError && (
    <span>
      <small className={ styles.error }>
        {fieldError.message}
      </small>
    </span>
  ), [fieldError])

  const displayValueChild = useMemo(() => !!displayValue && (
    <span className={ styles.displayValue }>
      { displayValue }
    </span>
  ), [displayValue])

  const classNames = cn(
    styles.radioButton,
    wrapperClassName, {
      [styles.isDisabled]: !!disabled,
      [styles.isHighlighted]: !!isHighlighted,
      [styles.isSelected]: !!isSelected,
      [styles.withBorder]: !!withBorder,
    }
  )

  return (
    <label className={ classNames }>
      <Flex gap='spread' horizontal>
        <Flex gap='tiny' horizontal>
          { hiddenRadioButton }
          <div className={ cn(styles.dot, dotClassName) } />
          <Flex gap='none' verticallyCenter>
            { labelTextChild }
            { hintChild }
          </Flex>
        </Flex>
        { displayValueChild }
      </Flex>
      { textChild || children }
      { fieldErrorChild }
    </label>
  )
}
